import * as React from "react";
import {useEffect, useRef, useState} from "react";
import {Button, Checkbox, Dropdown, Icon, Message, Popup} from 'semantic-ui-react';
import {authentication} from "../../../../../authentication";
import {backend} from "../../../../../xconvert-backend";
import {ConfigContext} from "../../../context/ConfigContext";
import {AddDefaultsToConfigRequest, CompanyConfiguration} from "../../../../../generated";
import {IfBox} from "../../../../style/if";
import {ConverterSendButton} from "../ConverterSendButton";
import {v4 as uuid} from 'uuid';
import {
    FileProcessingState,
    FileUploadMultiple,
    FileWithState,
    uploadedFiles
} from "../../../../util/FileUploadMultiple";
import {importSimOutputs} from "./ImportSimTabMenu";
import {updateConfig, workingConfig} from "../../configChange/ConfigSignal";
import {FileViewer} from "../../../../util/fileViewer/FileViewer";
import {ImportSimRepeatRunsModal, RepeatRun} from "./ImportSimRepeatRunsModal";

const REPEAT_COUNT_OPTIONS = [3, 5, 10]

export interface ImportSimFileProps {
}

export function ImportSimFile(props: ImportSimFileProps) {
    const context = React.useContext(ConfigContext)

    const [errorMessage, setErrorMessage] = React.useState(null)
    const [previewFile, setPreviewFile] = useState<FileWithState | null>(null);
    const [fileBuffer, setFileBuffer] = useState<ArrayBuffer | null>(null);
    const selectedFileNameRef = useRef<string | null>(null);

    const [repeatCount, setRepeatCount] = useState<number>(5)
    const [repeatRuns, setRepeatRuns] = useState<RepeatRun[]>([])
    const [repeatFile, setRepeatFile] = useState<FileWithState | null>(null)
    const [repeatRunning, setRepeatRunning] = useState<boolean>(false)
    const [repeatModalOpen, setRepeatModalOpen] = useState<boolean>(false)

    useEffect(() => {
        context.setConfSimInputActive("FILE")
    }, []);

    useEffect(() => {
        // Only reset to first file if current preview no longer exists in the list
        // This prevents jumping when updateFileState creates a new array
        const currentPreviewStillExists = previewFile &&
            uploadedFiles.value.some(f => f.fileName === previewFile.fileName);

        if (!currentPreviewStillExists) {
            setPreviewFile(uploadedFiles.value.length > 0 ? uploadedFiles.value[0] : null)
        }
    }, [uploadedFiles.value]);

    useEffect(() => {
        if (previewFile) {
            selectedFileNameRef.current = previewFile.fileName
            setFileBuffer(null)
            fileToArrayBuffer(previewFile.file)
                .then((result) => {
                    if (result && result instanceof ArrayBuffer) {
                        setFileBuffer(result)
                    }
                })
                .catch(error => {
                    console.error("FAILED TO CONVERT FILE TO ARRAYBUFFER: ", error);
                    setFileBuffer(null)
                });
        } else {
            selectedFileNameRef.current = null
            setFileBuffer(null)
        }
    }, [previewFile]);

    function isCacheDisabledInConfig(config: any): boolean {
        return config?.genericAiConfig?.aiProcessingSettings?.disableAiRequestsCache === true;
    }

    // Applies the flag to a genericAiConfig-shaped object and every nested override
    function applyCacheDisabledToGenericAiConfig(genericAiConfig: any, disabled: boolean) {
        if (!genericAiConfig) return;

        if (!genericAiConfig.aiProcessingSettings) {
            genericAiConfig.aiProcessingSettings = {};
        }
        genericAiConfig.aiProcessingSettings.disableAiRequestsCache = disabled;

        if (Array.isArray(genericAiConfig.specificConfigurations)) {
            genericAiConfig.specificConfigurations.forEach((sc: any) => {
                if (sc?.override) {
                    applyCacheDisabledToGenericAiConfig(sc.override, disabled);
                }
            });
        }
    }

    function getCurrentCacheDisabledState(): boolean {
        if (workingConfig.value) {
            try {
                return isCacheDisabledInConfig(JSON.parse(workingConfig.value));
            } catch (error) {
                console.error("Error parsing workingConfig:", error);
            }
        }
        return false;
    }

    /** repeat runs only make sense for GenericAi - same rule as the "Disable AI Cache" checkbox */
    function isGenericAiActive(): boolean {
        try {
            if (!workingConfig.value) return false
            const config: CompanyConfiguration = JSON.parse(workingConfig.value)
            if (config?.activeConverter?.toString() === "GENERIC_AI") return true
            return (config?.activeConverterOverride ?? [])
                .some((o: any) => o?.converter?.toString() === "GENERIC_AI")
        } catch (e) {
            return false
        }
    }

    function withAiCacheDisabled(config: any) {
        const clone = JSON.parse(JSON.stringify(config))
        if (!clone.genericAiConfig) clone.genericAiConfig = {}
        applyCacheDisabledToGenericAiConfig(clone.genericAiConfig, true)
        return clone
    }

    function fileToBase64Content(file: File): Promise<string> {
        return new Promise<string>((resolve, reject) => {
            const reader = new FileReader()
            reader.onload = () => resolve(String(reader.result).split(";base64,")[1])
            reader.onerror = error => reject(error)
            reader.readAsDataURL(file)
        })
    }

    /** working config (or company config as fallback) + backend defaults */
    async function resolveConfigWithDefaults() {
        const auth = backend.withTokenAuthHeader(authentication.token)

        let configWithoutDefaults = workingConfig.value
        if (!configWithoutDefaults) {
            console.log("config in context is null. Loading config from backend")
            const currentCompanyConfig = await backend.internalApi.fetchCompanyConfiguration(context.companyId, auth)
            configWithoutDefaults = JSON.stringify(currentCompanyConfig, null, 4)
            await updateConfig(configWithoutDefaults, false, false)
        }

        const request = {config: JSON.parse(configWithoutDefaults)} as AddDefaultsToConfigRequest
        return (await backend.internalApi.addDefaultsToConfig(request, auth)).config
    }

    async function callConverter(base64Data: string, config: any, fileName: string) {
        let url = '/converter/v3/convertExternalFormatToTransportOrder'
        if (context.customConverterBasePath) {
            url = context.customConverterBasePath + url
        }
        const xRequestId = uuid()

        const response = await fetch(url, {
            method: 'POST',
            headers: {
                "Content-Type": "application/json; charset=utf-8",
                'x-request-id': xRequestId
            },
            body: JSON.stringify({
                base64Data: base64Data,
                config: config,
                companyId: context.companyId,
                inputFileName: fileName
            })
        })

        return {
            xRequestId,
            fileName,
            TOs: await response.text(),
            status: response.status,
            statusText: response.statusText,
            ok: response.ok
        }
    }

    async function doConvert(file: FileWithState) {
        const base64Data = await fileToBase64Content(file.file)   // awaited - was a race before
        const config = await resolveConfigWithDefaults()
        const res = await callConverter(base64Data, config, file.fileName)

        if (res.status == 200) {
            context.setConfSimToSaveMode('REPLACE')
        } else if (res.status == 206) {
            context.setConfSimToSaveMode('PATCH')
        } else {
            context.setConfSimToSaveMode(null)
        }
        setErrorMessage(res.ok ? null : res.statusText)
        context.setConfSimInputLastType("FILE")

        return {xRequestId: res.xRequestId, fileName: res.fileName, TOs: res.TOs}
    }

    /** runs the previewed file N times in parallel, with the AI cache disabled */
    async function runRepeatedly() {
        const file = previewFile
        if (!file || repeatRunning) return

        setRepeatRunning(true)
        setRepeatFile(file)
        setRepeatModalOpen(true)
        setRepeatRuns(Array.from({length: repeatCount}, (_, i) => ({nr: i + 1, state: "RUNNING" as const})))

        const patchRun = (nr: number, patch: Partial<RepeatRun>) =>
            setRepeatRuns(prev => prev.map(r => r.nr === nr ? {...r, ...patch} : r))

        try {
            // file and config are resolved once -> every run is identical, differences come from the AI only
            const base64Data = await fileToBase64Content(file.file)
            const config = withAiCacheDisabled(await resolveConfigWithDefaults())

            await Promise.all(Array.from({length: repeatCount}, async (_, i) => {
                const nr = i + 1
                const started = Date.now()
                try {
                    const res = await callConverter(base64Data, config, file.fileName)
                    patchRun(nr, {
                        state: res.ok ? "DONE" : "ERROR",
                        xRequestId: res.xRequestId,
                        output: res.TOs,
                        error: res.ok ? undefined : `${res.status} ${res.statusText}`,
                        durationMs: Date.now() - started
                    })
                } catch (e) {
                    console.error("repeat run failed: ", nr, e)
                    patchRun(nr, {state: "ERROR", error: String(e), durationMs: Date.now() - started})
                }
            }))
        } catch (e) {
            console.error("could not start repeat runs: ", e)
            setRepeatRuns(prev => prev.map(r => r.state === "RUNNING"
                ? {...r, state: "ERROR" as const, error: String(e)} : r))
        } finally {
            setRepeatRunning(false)
        }
    }

    function updateFileState(file, newState) {
        uploadedFiles.value = uploadedFiles.value.map((f) =>
            f.fileName === file.fileName ? {file: f.file, fileName: f.fileName, state: newState} : f)
    }

    function removeFile(fileToRemove: FileWithState) {
        const newFiles = uploadedFiles.value.filter((f) => f.fileName !== fileToRemove.fileName)
        uploadedFiles.value = newFiles

        const newOutputs = importSimOutputs.value.filter(
            (output: any) => output.result.fileName !== fileToRemove.fileName
        )

        if (newOutputs.length > 0 && !newOutputs.some((o: any) => o.selected)) {
            newOutputs[0].selected = true
            selectedFileNameRef.current = newOutputs[0].result.fileName
        }
        if (newOutputs.length === 0) {
            selectedFileNameRef.current = null
        }
        importSimOutputs.value = newOutputs

        if (previewFile?.fileName === fileToRemove.fileName) {
            setPreviewFile(newFiles.length > 0 ? newFiles[0] : null)
        }
    }

    function addResultToOutputs(result: any) {
        const currentlySelectedName = selectedFileNameRef.current
        const currentOutputs = [...importSimOutputs.value]

        const isFirstResult = currentOutputs.length === 0 && currentlySelectedName === null
        currentOutputs.push({result: result, selected: isFirstResult})

        if (isFirstResult) {
            selectedFileNameRef.current = result.fileName
        }

        const selectedName = selectedFileNameRef.current
        importSimOutputs.value = currentOutputs.map((o: any) => ({
            ...o,
            selected: o.result.fileName === selectedName
        }))
    }

    async function convertAllFiles() {
        importSimOutputs.value = []
        selectedFileNameRef.current = null

        const filesToProcess = [...uploadedFiles.value]

        await Promise.all(filesToProcess.map(async (file) => {
            updateFileState(file, "PROCESSING")
            try {
                const result = await doConvert(file)
                updateFileState(file, "PROCESSED")
                addResultToOutputs(result)
                return result
            } catch (error) {
                console.error("Error converting file: ", file.fileName, error)
                updateFileState(file, "NOT_PROCESSED")
                return null
            }
        }))
    }

    function getReadableFileSizeString(file: File) {
        if (file == null) {
            return 0
        }
        let fileSizeInBytes = file.size
        let i = -1;
        const byteUnits = [' kB', ' MB', ' GB', ' TB', 'PB', 'EB', 'ZB', 'YB'];
        do {
            fileSizeInBytes = fileSizeInBytes / 1024;
            i++;
        } while (fileSizeInBytes > 1024);

        return Math.max(fileSizeInBytes, 0.1).toFixed(1) + byteUnits[i];
    }

    function drawState(state: FileProcessingState) {
        switch (state) {
            case "NOT_PROCESSED":
                return <Icon name={"question"} color={"red"}/>
            case "PROCESSING":
                return <Icon name={"spinner"} loading color={"grey"}/>
            case "PROCESSED":
                return <Icon name={"check"} color={"green"}/>
        }
    }

    function drawFileList() {
        return <ul>
            {uploadedFiles.value.map((file, i) => (
                <li key={file.fileName + "_" + i}>
                    {file.fileName} - {getReadableFileSizeString(file.file)} {drawState(file.state)}
                    <Button
                        icon={"eye"}
                        color={viewFileButtonColor(file)}
                        onClick={() => {
                            setPreviewFile(file)
                            selectedFileNameRef.current = file.fileName
                            if (importSimOutputs.value.length > 0) {
                                importSimOutputs.value = importSimOutputs.value.map((output: any) => ({
                                    ...output,
                                    selected: output.result.fileName === file.fileName
                                }))
                            }
                        }}
                    />
                    {uploadedFiles.value.length > 1 && (
                        <Button
                            icon={"trash"}
                            color={"red"}
                            onClick={() => removeFile(file)}
                            title={"Remove file"}
                        />
                    )}
                </li>
            ))}
        </ul>
    }

    function viewFileButtonColor(file: FileWithState) {
        return previewFile?.fileName === file.fileName ? "blue" : "grey"
    }

    function fileToArrayBuffer(file) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = event => resolve(event.target.result);
            reader.onerror = error => reject(error);
            return reader.readAsArrayBuffer(file);
        });
    }

    function drawFilePreview() {
        if (previewFile == null || fileBuffer == null) {
            return null
        }
        return <FileViewer
            key={previewFile.fileName + "_" + previewFile.file.size}
            fileBuffer={fileBuffer}
            fileName={previewFile.fileName}
            displayName={true}
        />
    }

    function drawRepeatRunControls() {
        if (!isGenericAiActive()) return null

        return <span style={{
            marginLeft: '20px',
            paddingLeft: '20px',
            borderLeft: '1px solid #e0e0e0',
            verticalAlign: 'middle'
        }}>
            <Button
                size={"small"}
                content={"Repeat run"}
                title={"Runs the selected file multiple times with disabled AI cache to check if the output is stable"}
                loading={repeatRunning}
                disabled={previewFile == null || repeatRunning}
                onClick={runRepeatedly}
            />
            <Dropdown
                inline
                disabled={repeatRunning}
                options={REPEAT_COUNT_OPTIONS.map(c => ({key: c, value: c, text: `${c}×`}))}
                value={repeatCount}
                onChange={(_, d) => setRepeatCount(d.value as number)}
                style={{marginLeft: '4px'}}
            />
            {repeatRuns.length > 0 && !repeatModalOpen && <Button
                size={"small"}
                basic
                content={"Results"}
                style={{marginLeft: '10px'}}
                onClick={() => setRepeatModalOpen(true)}
            />}
        </span>
    }

    return <>
        <div>
            <ConverterSendButton
                onClick={convertAllFiles}
                disabled={uploadedFiles.value.length == 0}
            />
            <IfBox shouldShow={errorMessage != null}>
                <Message negative={true} compact={true}>
                    <Message.Header>ERROR:</Message.Header>
                    <Message.Content>{errorMessage}</Message.Content>
                </Message>
            </IfBox>

            <FileUploadMultiple/>
            <Checkbox
                checked={getCurrentCacheDisabledState()}
                label="Disable AI Cache" //Only works for GenericAi as activeConverter.
                onChange={async (e, data) => {
                    if (workingConfig.value) {
                        try {
                            const config = JSON.parse(workingConfig.value);
                            if (!config.genericAiConfig) {
                                config.genericAiConfig = {};
                            }
                            applyCacheDisabledToGenericAiConfig(config.genericAiConfig, data.checked);
                            await updateConfig(JSON.stringify(config, null, 4), false, false);
                        } catch (error) {
                            console.error("Error updating config:", error);
                        }
                    }
                }}
                style={{marginLeft: '15px', verticalAlign: 'middle'}}
            />

            {drawRepeatRunControls()}
        </div>
        {drawFileList()}
        {drawFilePreview()}

        <ImportSimRepeatRunsModal
            isOpen={repeatModalOpen}
            onClose={() => setRepeatModalOpen(false)}
            fileName={repeatFile?.fileName ?? null}
            inputFile={repeatFile?.file ?? null}
            runs={repeatRuns}
            running={repeatRunning}
        />
    </>
}